import { describeMutationError } from "@api/mutationErrors";
import { ClinicAuthError } from "@auth/clinicAuthClient";
import { useSessionKey } from "@auth/sessionStore";
import { useExamScreenRoomIds } from "@displays/displaysQueries";
import {
  Button,
  CheckIcon,
  Chip,
  cn,
  FieldError,
  formatClockTime,
  Popover,
  QuestionCircleIcon,
} from "@scribenote/brian-design-system";
import {
  isConfirmedIn,
  isVisitCancelled,
  type TodayVisit,
} from "@visits/visitsClient";
import { useBoardVisits } from "@visits/visitsQueries";
import { type ReactNode, useRef, useState } from "react";

import {
  useConfirmRoomVisit,
  useReleaseRoomVisit,
  useRoomsListQuery,
} from "./roomsQueries";

// Opts back in over a card's whole-card button (DESIGN.md, whole-card targets).
const OVER_CARD =
  "pointer-events-auto relative rounded-swipe focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent";

const leftRoom = (error: unknown): boolean =>
  error instanceof ClinicAuthError &&
  error.status === 409 &&
  error.detail === "visit_not_in_room";

const ConfirmButton = ({
  visit,
  roomUuid,
  includeHousehold,
  ariaLabel,
  confirm,
  children,
}: {
  visit: TodayVisit;
  roomUuid: string;
  includeHousehold: boolean;
  ariaLabel: string;
  confirm: ReturnType<typeof useConfirmRoomVisit>;
  children: ReactNode;
}) => (
  <button
    type="button"
    aria-label={ariaLabel}
    disabled={confirm.isPending}
    onClick={() =>
      confirm.mutate({ roomUuid, visitUuid: visit.id, includeHousehold })
    }
    className={cn(OVER_CARD, "cursor-pointer disabled:opacity-60")}
  >
    <Chip
      tone="attention"
      size="sm"
      icon={<QuestionCircleIcon />}
      className="px-1.5 pt-px pb-0.5"
    >
      {children}
    </Chip>
  </button>
);

const useUnconfirmedSiblings = (visit: TodayVisit, roomUuid: string) =>
  useBoardVisits(useSessionKey()).filter(
    (other) =>
      other.id !== visit.id &&
      other.householdId === visit.householdId &&
      other.currentRoomUuid === roomUuid &&
      !isVisitCancelled(other) &&
      !isConfirmedIn(other, roomUuid),
  );

/** One tap for the household's unconfirmed visits in the room (BRI-377). */
const HouseholdConfirmChip = ({
  visit,
  name,
  roomUuid,
  roomName,
  confirm,
}: {
  visit: TodayVisit;
  name: string;
  roomUuid: string;
  roomName: string;
  confirm: ReturnType<typeof useConfirmRoomVisit>;
}) => {
  const siblings = useUnconfirmedSiblings(visit, roomUuid);
  if (siblings.length === 0) return null;
  const names = [name, ...siblings.map((s) => s.patientName)].join(", ");
  return (
    <ConfirmButton
      visit={visit}
      roomUuid={roomUuid}
      includeHousehold
      ariaLabel={`Confirm ${names} are in ${roomName}`}
      confirm={confirm}
    >
      {siblings.length === 1 ? "Confirm both" : "Confirm all"}
    </ConfirmButton>
  );
};

/** Asks "is this the patient in this room?" before its exam screen shows the record (BRI-103). */
const AskChip = ({
  visit,
  name,
  roomUuid,
  roomName,
  label,
  offerHousehold,
}: {
  visit: TodayVisit;
  name: string;
  roomUuid: string;
  roomName: string;
  label: string;
  offerHousehold: boolean;
}) => {
  const confirm = useConfirmRoomVisit();
  return (
    <>
      <ConfirmButton
        visit={visit}
        roomUuid={roomUuid}
        includeHousehold={false}
        ariaLabel={`Confirm ${name} is in ${roomName}`}
        confirm={confirm}
      >
        {label}
      </ConfirmButton>
      {offerHousehold && (
        <HouseholdConfirmChip
          visit={visit}
          name={name}
          roomUuid={roomUuid}
          roomName={roomName}
          confirm={confirm}
        />
      )}
      {confirm.isError && (
        <FieldError className="pointer-events-auto relative basis-full text-xs">
          {leftRoom(confirm.error)
            ? `${name} isn’t in ${roomName} anymore.`
            : describeMutationError(confirm.error, "confirm the room")}
        </FieldError>
      )}
    </>
  );
};

const ConfirmedChip = ({
  visit,
  name,
  roomUuid,
  roomName,
  label,
}: {
  visit: TodayVisit;
  name: string;
  roomUuid: string;
  roomName: string;
  label: string;
}) => {
  const release = useReleaseRoomVisit();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const confirmation = visit.roomConfirmation;
  const by = confirmation?.confirmedByName;
  const at =
    confirmation && formatClockTime(new Date(confirmation.confirmedAt));

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        aria-label={`${roomName}, confirmed for ${name}`}
        aria-expanded={open}
        onClick={() => setOpen((was) => !was)}
        className={cn(
          OVER_CARD,
          "inline-flex cursor-pointer items-center gap-1 text-annotation hover:text-ink",
        )}
      >
        <CheckIcon aria-hidden="true" className="size-3.5 text-ink-recorded" />
        {label}
      </button>
      <Popover
        open={open}
        onOpenChange={setOpen}
        anchorRef={anchorRef}
        className="flex w-64 flex-col gap-2.5 p-3"
      >
        <p className="text-[0.8125rem] text-ink-body">
          {by ? `Confirmed by ${by}` : "Confirmed"}
          {at ? ` at ${at}` : ""}. Release {roomName} if this isn’t {name}.
        </p>
        <Button
          variant="ghost"
          size="sm"
          busy={release.isPending}
          className="self-start"
          onClick={() =>
            release.mutate(
              { roomUuid, visitUuid: visit.id },
              { onSuccess: () => setOpen(false) },
            )
          }
        >
          Release {roomName}
        </Button>
        {release.isError && (
          <FieldError className="text-xs">
            {describeMutationError(release.error, "release the room")}
          </FieldError>
        )}
      </Popover>
    </>
  );
};

type RoomAsk =
  | { kind: "ask"; roomUuid: string }
  | { kind: "confirmed"; roomUuid: string }
  | { kind: "none" };

/** The current room's ask, and a confirmation held for another room while the patient is away. */
const useRoomAsk = (visit: TodayVisit) => {
  const sessionKey = useSessionKey();
  const examScreenRooms = useExamScreenRoomIds(sessionKey);
  const { data: rooms } = useRoomsListQuery(sessionKey, undefined, true);
  const here = visit.currentRoomUuid;
  const held = visit.roomConfirmation?.roomId ?? null;

  const current: RoomAsk =
    here === null
      ? { kind: "none" }
      : isConfirmedIn(visit, here)
        ? { kind: "confirmed", roomUuid: here }
        : examScreenRooms?.has(here)
          ? { kind: "ask", roomUuid: here }
          : { kind: "none" };
  const away =
    held !== null && held !== here && current.kind !== "ask"
      ? {
          roomUuid: held,
          roomName: rooms?.find((r) => r.id === held)?.name ?? "Room",
        }
      : null;
  return { current, away };
};

// The dot rides at the end of the part before it, so a wrapped line never starts with one.
const joinDots = (parts: ReactNode[]): ReactNode =>
  parts.map((part, i) => (
    <span key={i} className="inline-flex items-center gap-1">
      {part}
      {i < parts.length - 1 && <span aria-hidden="true">·</span>}
    </span>
  ));

/** A board card's who line, with the room as the confirm control. */
export const RoomWho = ({
  visit,
  name,
  roomName,
  rest,
  readOnly = false,
}: {
  visit: TodayVisit;
  name: string;
  roomName: string | null;
  rest: string | null;
  /** Plain text, an unconfirmed room marked `?`, as the perception roster does. */
  readOnly?: boolean;
}) => {
  const { current, away } = useRoomAsk(visit);
  const room =
    roomName === null ? null : current.kind === "ask" ? (
      readOnly ? (
        `${roomName}?`
      ) : (
        <AskChip
          visit={visit}
          name={name}
          roomUuid={current.roomUuid}
          roomName={roomName}
          label={roomName}
          offerHousehold
        />
      )
    ) : current.kind === "confirmed" && !readOnly ? (
      <ConfirmedChip
        visit={visit}
        name={name}
        roomUuid={current.roomUuid}
        roomName={roomName}
        label={roomName}
      />
    ) : (
      roomName
    );
  const held = away ? (
    readOnly ? (
      away.roomName
    ) : (
      <ConfirmedChip
        visit={visit}
        name={name}
        roomUuid={away.roomUuid}
        roomName={away.roomName}
        label={away.roomName}
      />
    )
  ) : null;
  const parts = [room, held, rest].filter((p) => p != null);
  if (parts.length === 0) return null;
  return (
    <span className="inline-flex flex-wrap items-center gap-x-1 gap-y-0.5">
      {joinDots(parts)}
    </span>
  );
};

/** A front desk room tile names the room already, so the chip only says what to do. */
export const RoomTileConfirm = ({
  visit,
  name,
  roomName,
  label,
  offerHousehold = false,
}: {
  visit: TodayVisit;
  name: string;
  roomName: string;
  /** Names the pet when the tile holds more than one. */
  label?: string;
  /** The tile shows "Confirm both" once, on the pet the caller picks. */
  offerHousehold?: boolean;
}) => {
  const { current, away } = useRoomAsk(visit);
  const chips: ReactNode[] = [];
  if (current.kind === "ask")
    chips.push(
      <AskChip
        key="ask"
        visit={visit}
        name={name}
        roomUuid={current.roomUuid}
        roomName={roomName}
        label={label ? `Confirm ${label}` : "Confirm"}
        offerHousehold={offerHousehold}
      />,
    );
  if (current.kind === "confirmed")
    chips.push(
      <ConfirmedChip
        key="confirmed"
        visit={visit}
        name={name}
        roomUuid={current.roomUuid}
        roomName={roomName}
        label={label ? `${label} confirmed` : "Confirmed"}
      />,
    );
  if (away)
    chips.push(
      <ConfirmedChip
        key="away"
        visit={visit}
        name={name}
        roomUuid={away.roomUuid}
        roomName={away.roomName}
        label={away.roomName}
      />,
    );
  if (chips.length === 0) return null;
  return (
    <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[0.8125rem]">
      {chips}
    </span>
  );
};
